import React from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	FormHelperText,
	Box,
	Typography,
	Stack,
	useTheme,
} from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useAutomationContext } from '@/context/automationContext';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/utils/firebase';
import { AI_DISCLAIMER, delay } from '@/utils/constants';
import { STORAGE_PURPOSES, dealStoragePath } from '@/utils/storagePaths';
import FileUpload from '@/components/FileUpload';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { closeDialog } from '@/features/dialog/dialogSlice';
import {
	AI_SUMMARY_TYPES,
	factFindApi,
	useCreateDealProposalMutation,
} from '@/features/factFind/factFindApi';
import quoteJson from '@/assets/json/quote.json';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useMsGraph } from '@/hooks/useMsGraph';
import FolioCheckbox from '@/components/FolioCheckbox';
import { AI_WORKFLOW_TYPES } from '@/features/aiWorkflow/constants';
import { useCreateAIWorkflowMutation } from '@/features/aiWorkflow/aiWorkflowApi';
import { showToast } from '@/features/toast/toastSlice';
import { convertFilesForUpload } from '@/features/msGraph/helper';
import { AUTOMATION_NAMES } from '@/utils/automationCatalog';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
	files: Yup.array().min(1, 'Please upload at least one file'),
	errorMessage: Yup.string(),
});

const QUOTE_TEXT =
	'Upload insurer quote documents to quickly prepare them for your Summary and SOA!';
const WYTU_TEXT =
	'Upload your fact find records and let Folio AI organise them, ready to get for quotes';

const ALLOWED_DOCUMENT_TYPES = ['pdf', 'xls', 'xlsx', 'csv', 'doc', 'docx']; // 'png', // remove png for now

const AutomationText = () => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const { getFeatureFlags } = useFeatureFlagContext();
	const { getAutomations } = useAutomationContext();
	const showAutomations = Boolean(getFeatureFlags('displayAutomations')?.active);
	const preRenewalBasic = !showAutomations
		? true
		: Boolean(getAutomations(AUTOMATION_NAMES.preRenewalBasic)?.enabled);
	const detailedPreRenewal = Boolean(
		getAutomations(AUTOMATION_NAMES.detailedPreRenewal)?.enabled,
	);

	const types = [
		...(preRenewalBasic ? [AI_WORKFLOW_TYPES.preRenewalBasic] : []),
		...(detailedPreRenewal ? [AI_WORKFLOW_TYPES.riskInfoRenewalEmail] : []),
	];

	if (types.length === 0) {
		return <></>;
	}

	return (
		<Typography
			fontSize={'12px'}
			sx={{ color: isDarkMode ? theme.palette.folioWhite65 : theme.palette.folioRegular }}
		>
			<span>{'This will also start the workflow for generating a '}</span>
			{types.map((type, index) => (
				<span key={type}>
					{index > 0 && <span>{' and '}</span>}
					<span style={{ fontWeight: 'bold' }}>{type}</span>
				</span>
			))}
			<span>{' based on the risk info document(s) you have uploaded.'}</span>
		</Typography>
	);
};

export const AddProposalDocumentDialog = ({ documentType }) => {
	const isQuote = documentType.toUpperCase() === 'QUOTE';
	const lowercaseType = documentType.toLowerCase();

	const dispatch = useDispatch();

	const open = useSelector((state) => state.dialog.open[lowercaseType]);

	const handleClose = () => dispatch(closeDialog(lowercaseType));

	const label = isQuote ? documentType : 'Risk Info';

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			slotProps={{
				paper: {
					sx: {
						minWidth: { xs: '100%', sm: '70%', md: '50%' },
					},
				},
			}}
		>
			<AddProposalDocumentForm documentType={documentType}>
				<DialogTitle>{`Upload ${label}`}</DialogTitle>
				<DialogContent>
					<AddProposalDocumentContent documentType={documentType} />
				</DialogContent>
				<AddProposalDocumentActions documentType={documentType} />
			</AddProposalDocumentForm>
		</Dialog>
	);
};

const AddProposalDocumentActions = ({ documentType }) => {
	const dispatch = useDispatch();

	const { isSubmitting, resetForm } = useFormikContext();

	const lowercaseType = documentType.toLowerCase();

	const handleClose = () => dispatch(closeDialog(lowercaseType));

	return (
		<DialogActions>
			<Button
				onClick={(e) => {
					e.preventDefault();
					resetForm();
					handleClose();
				}}
				color='primary'
			>
				Cancel
			</Button>
			<Button color='primary' type='submit' variant='contained' disabled={isSubmitting}>
				{isSubmitting ? (
					<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
				) : (
					'Create'
				)}
			</Button>
		</DialogActions>
	);
};

const AddProposalDocumentForm = ({ documentType, children }) => {
	const { dealId, clientRow, dealRow } = useObjectContext();

	const isQuote = documentType.toUpperCase() === 'QUOTE';
	const lowercaseType = documentType.toLowerCase();

	const summaryTypes = isQuote
		? [AI_SUMMARY_TYPES.quote, AI_SUMMARY_TYPES.quoteSummary]
		: [AI_SUMMARY_TYPES.wytu, AI_SUMMARY_TYPES.riskInfoSummary];

	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getDealDriveId } = useClientStorageHooks(dealRow?.brokerGroupId);

	const [createDealProposal] = useCreateDealProposalMutation();
	const [createAIWorkflow] = useCreateAIWorkflowMutation();

	const { uploadFilesAndRetry } = useMsGraph();

	const handleClose = () => dispatch(closeDialog(lowercaseType));

	const label = isQuote ? documentType : 'Risk Info';

	const createFirebaseDocument = async (values) => {
		const docId = await createDealProposal({
			dealId,
			type: lowercaseType,
			generating: true,
			jsonData: isQuote ? quoteJson : {},
			...(values.emailUser && { emailUser: values.emailUser }),
		})
			.unwrap()
			.catch((err) => console.log('Error creating Firebase document', err));
		console.log('DOC', docId);
		return docId;
	};

	const createWorkflows = async (filePaths, emailUser) => {
		for (const workflowType of summaryTypes) {
			await createAIWorkflow({
				dealId,
				workflowType,
				filePaths,
				emailUser,
			})
				.unwrap()
				.then((res) => {
					console.log('Result of creating workflow', res);
				})
				.catch((err) => {
					console.log('Error creating workflow', err);
					dispatch(
						showToast({
							message: `Error creating ${label} AI extraction workflow; please try again.`,
							error: true,
						}),
					);
				});
			if (summaryTypes.length > 1) {
				await delay();
			}
		}
	};

	const uploadFile = async ({ file, docId, emailUser }) => {
		const fileObj = file.file || file;
		const filePath = dealStoragePath(
			{ clientDocId: dealRow?.clientDocId, dealDocId: dealId },
			isQuote ? STORAGE_PURPOSES.quotes : STORAGE_PURPOSES.riskInfo,
			fileObj.name,
		);
		const storageRef = ref(storage, filePath);

		const metadata = {
			customMetadata: {
				'Deal ID': dealId.toString(),
				'AI Document Type': isQuote ? 'Quote' : 'WYTU',
				'User ID': userDetails?.userId,
				...(docId && { 'Firebase Document ID': docId }),
				...(emailUser && { 'Email User': 'true' }),
			},
		};

		await uploadBytes(storageRef, fileObj, metadata);
		return filePath;
	};

	const uploadFilesToStorage = async (values) => {
		const { driveId } = await getDealDriveId(dealId);
		const docId = await createFirebaseDocument(values);

		const filePaths = [];
		const resolvedFiles = await convertFilesForUpload(values.files);

		const sharepointFiles = resolvedFiles
			.filter((file) => driveId && !file.isSharepointFile)
			.map((file) => file.file || file);

		if (sharepointFiles.length > 0) {
			await uploadFilesAndRetry({
				driveId: dealRow?.clientFolderDriveId,
				itemId: driveId,
				files: sharepointFiles,
			});
		}

		for (const file of resolvedFiles) {
			const filePath = await uploadFile({ file, docId, emailUser: values.emailUser });
			filePaths.push(filePath);
		}

		setTimeout(
			async () => await createWorkflows(filePaths.filter(Boolean), values.emailUser),
			500,
		);

		setTimeout(async () => {
			try {
				dispatch(
					factFindApi.util.invalidateTags([{ type: 'DEAL_NOTE_SUMMARY', id: dealId }]),
				);
				dispatch(triggerRefresh());
			} catch (error) {
				console.error('Error invalidating API:', error);
			}
		}, 2000);
	};

	return (
		<Formik
			initialValues={{
				files: [],
				errorMessage: '',
				deal: dealRow,
				client: clientRow,
			}}
			validationSchema={validationSchema}
			enableReinitialize={true}
			validateOnBlur={true}
			onSubmit={async (values, { setSubmitting, resetForm, setFieldValue }) => {
				setSubmitting(true);
				try {
					await uploadFilesToStorage(values);
					console.log(`🚀 ~ Successfully added ${documentType}`);
					resetForm();
					handleClose();
				} catch (err) {
					console.log(`🙅 ~ Error creating ${documentType}`, err);
					setFieldValue(
						'errorMessage',
						`Error uploading ${label} document. Please try again.`,
					);
				}
				setSubmitting(false);
			}}
		>
			<Form>{children}</Form>
		</Formik>
	);
};
const AddProposalDocumentContent = ({ documentType }) => {
	const { values, setFieldValue, isSubmitting } = useFormikContext();
	const { dealRow } = useObjectContext();
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';

	const isQuote = documentType.toUpperCase() === 'QUOTE';

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const isRenewalDeal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;

	const textColour = isDarkMode ? theme.palette.folioWhite : theme.palette.folioBlack100;
	const secondaryTextColour = isDarkMode
		? theme.palette.folioWhite65
		: theme.palette.folioRegular;

	const helpfulHint = isQuote ? QUOTE_TEXT : WYTU_TEXT;

	return (
		<>
			<Box
				sx={{
					width: '100%',
					backgroundColor: isDarkMode ? theme.palette.darkModeSideNav : '#D9DCFE',
					borderRadius: '4px',
					alignItems: 'center',
					border: isDarkMode ? '1.5px solid #7B83FD' : '2px solid #7B83FD',
					padding: '1em',
					mb: '2em',
				}}
			>
				<Typography variant='body1' sx={{ color: secondaryTextColour }}>
					<Box
						component='span'
						sx={{ fontWeight: 700, paddingRight: '0.5em', color: textColour }}
					>
						Helpful tip:
					</Box>
					{helpfulHint}
				</Typography>
			</Box>
			<FileUpload
				files={values.files || []}
				setFiles={(files) => setFieldValue('files', files)}
				types={ALLOWED_DOCUMENT_TYPES}
				multiple={true}
				loading={isSubmitting}
				attachFromSharepoint={true}
			/>
			{values.errorMessage?.length > 0 && (
				<FormHelperText error>{values.errorMessage}</FormHelperText>
			)}
			{!isQuote && values.files?.length > 0 && (
				<Stack spacing={2} pt={2}>
					{isRenewalDeal && <AutomationText />}
					<FolioCheckbox
						checked={Boolean(values.emailUser)}
						label={'I would like to receive an email once the workflow has finished'}
						onChange={() => setFieldValue('emailUser', !values.emailUser)}
					/>
					{!values.emailUser && (
						<Typography fontSize={'12px'} sx={{ color: secondaryTextColour }}>
							{`An email will be sent to your email address (${email}).`}
						</Typography>
					)}
				</Stack>
			)}
			<FormHelperText sx={{ pt: 2, ml: 0, color: secondaryTextColour }}>
				{AI_DISCLAIMER}
			</FormHelperText>
		</>
	);
};
